import { useEffect, useState } from "react"
import { createContext, useContext } from "react"
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid"

// Toast types
export type ToastType = "success" | "error" | "info"

type ToastOptions = {
  isLoading?: boolean
  /** Called when the toast goes away WITHOUT the creator removing it (close
   *  button, duration expiry) — lets owners of persistent toasts drop their
   *  stale id reference. Not called on programmatic removeToast. */
  onDismiss?: () => void
}

// Toast item interface
export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
  isLoading?: boolean
  onDismiss?: () => void
}

// Context for managing toasts
interface ToastContextProps {
  addToast: (message: string, type: ToastType, duration?: number, options?: ToastOptions) => string
  updateToast: (id: string, message: string, type?: ToastType, options?: ToastOptions) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined)

// Provider component
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = (
    message: string,
    type: ToastType = "info",
    duration = 5000,
    options?: ToastOptions,
  ) => {
    const id = crypto.randomUUID()
    setToasts((prev) => [...prev, { id, message, type, duration, ...options }])
    return id
  }

  const updateToast = (id: string, message: string, type?: ToastType, options?: ToastOptions) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id
          ? { ...toast, message, ...(type !== undefined && { type }), ...(options ?? {}) }
          : toast
      )
    )
  }

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  // Create context value
  const contextValue: ToastContextProps = {
    addToast,
    updateToast,
    removeToast,
  }

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}

// Hook for using toasts in components
export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider")
  }
  return context
}

// Toast container component
function ToastContainer({
  toasts,
  removeToast,
}: {
  toasts: Toast[]
  removeToast: (id: string) => void
}) {
  return (
    <div className="pointer-events-none fixed right-0 bottom-0 z-50 flex flex-col items-end space-y-2 p-4 print:hidden">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={() => removeToast(toast.id)}
        />
      ))}
    </div>
  )
}

// Individual toast component
function ToastItem({
  toast,
  onRemove,
}: {
  toast: Toast
  onRemove: () => void
}) {
  const dismiss = () => {
    toast.onDismiss?.()
    onRemove()
  }

  useEffect(() => {
    if (toast.duration) {
      const timer = setTimeout(() => {
        toast.onDismiss?.()
        onRemove()
      }, toast.duration)
      return () => clearTimeout(timer)
    }
  }, [toast, onRemove])

  // Toast styling based on type
  const getTypeStyles = (type: ToastType, isLoading?: boolean) => {
    if (isLoading) {
      return {
        icon: (
          <span
            aria-hidden="true"
            className="inline-block size-5 animate-spin rounded-full border-2 border-gold/25 border-t-gold"
          />
        ),
        borderColor: "border-[var(--color-gold)]/20",
      }
    }

    switch (type) {
      case "success":
        return {
          icon: (
            <CheckCircleIcon className="size-5 text-(--color-gold-icon)" />
          ),
          borderColor: "border-[var(--color-gold)]/30 dark:border-[var(--color-gold)]/20",
        }
      case "error":
        return {
          icon: <ExclamationCircleIcon className="size-5 text-zinc-500 dark:text-zinc-400" />,
          borderColor: "border-zinc-300/50 dark:border-zinc-700/20",
        }
      default:
        return {
          icon: <InformationCircleIcon className="size-5 text-zinc-400" />,
          borderColor: "border-zinc-700/20",
        }
    }
  }

  const { icon, borderColor } = getTypeStyles(toast.type, toast.isLoading)

  return (
    <div
      className={`flex items-start p-4 mb-2 rounded-lg bg-white/95 dark:bg-zinc-800/90 backdrop-blur-sm shadow-lg border ${borderColor} text-[var(--text-secondary)] dark:text-zinc-300 max-w-md transform transition-all duration-300 ease-in-out pointer-events-auto`}
      role="alert"
    >
      <div className="mr-3 shrink-0">{icon}</div>
      <div className="mr-2 flex-1 text-sm">{toast.message}</div>
      <button
        onClick={dismiss}
        className="shrink-0 text-zinc-400 transition-colors hover:text-zinc-600 dark:hover:text-zinc-300"
        aria-label="Close"
      >
        <XMarkIcon className="size-5" />
      </button>
    </div>
  )
}
